/**
 * ConfirmModal — drop-in replacement for `window.confirm` on flows where the
 * native browser dialog is wrong for one or more reasons:
 *
 *   1. Native confirm() ignores page styling — looks foreign next to the
 *      portal's design language.
 *   2. Native confirm() blocks the JS thread, which interacts badly with
 *      React's effect/refetch cycle and is the suspected cause of the
 *      "Approve & Send needs two clicks" report.
 *   3. Native confirm() can't show a destructive vs. CTA distinction, can't
 *      surface the rollup totals inline, can't disable on busy.
 *
 * Used by the Approve & Send paths (Dashboard + PCO detail page). Other
 * window.confirm sites (Mark complete, Reopen, Settings deletes) can adopt
 * this incrementally — no rush.
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When the action is destructive we color the confirm button danger. */
  destructive?: boolean;
  /** Disable both buttons while a parent-owned async action is in flight. */
  busy?: boolean;
  /** Label to show on the confirm button while `busy` is true. */
  busyLabel?: string;
  /**
   * One-button form: only the cancel button renders (label it with
   * `cancelLabel`, e.g. "Close"), and Enter never confirms. For the case
   * where the question turns out to have nothing to act on.
   */
  acknowledgeOnly?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive,
  busy,
  busyLabel,
  acknowledgeOnly,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  // Body scroll locks while open — refcounted, so this nests safely inside
  // another modal (ConfirmModal is frequently the inner one).
  useBodyScrollLock(open);

  // Esc closes; Enter confirms.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
      // Enter on a focused <button> already fires that button's click — it is
      // the keydown's default action — so answering here as well sends the
      // dialog's answer TWICE. The confirm button is autofocused, which makes
      // that the normal path: on Project Details it meant two PATCHes. Escape
      // is handled above the guard because Escape has no such default and the
      // focus is almost always on a button.
      if ((e.target as HTMLElement | null)?.closest('button')) return;
      if (e.key === 'Enter' && !busy && !acknowledgeOnly) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, acknowledgeOnly, onCancel, onConfirm]);

  if (!open) return null;

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-modal-head">
          <div className="confirm-modal-title">{title}</div>
        </div>
        <div className="confirm-modal-body">{body}</div>
        <div className="confirm-modal-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={busy}
            autoFocus={acknowledgeOnly}
          >
            {cancelLabel}
          </button>
          {!acknowledgeOnly && (
            <button
              type="button"
              className={destructive ? 'btn btn-danger' : 'btn btn-cta'}
              onClick={onConfirm}
              disabled={busy}
              autoFocus
            >
              {busy && busyLabel ? busyLabel : confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
